/**
 * Registers the project editor's UI intents. Idempotent — called once at app
 * startup. Owns the editor's `study.*` intents.
 *
 * - `study.proposeLesson`: the agent's propose_lesson tool emits it
 *   mid-conversation with the `channel_id` of the Study board chat; the
 *   handler records the proposal under that channel so the board's
 *   CaptureCard can offer Keep / Edit / Dismiss.
 * - `study.revisionNotesChanged`: the add_revision_note tool emits it after
 *   leaving a note; the handler bumps that document's notes signal so the open
 *   editor refetches.
 */
import { registerIntent } from '@features/agentUi/uiIntentDispatcher';

import { useEditorNotesSignal } from './stores/useEditorNotesSignal';
import { useLessonCaptureStore } from './stores/useLessonCaptureStore';

let registered = false;

/** A non-empty string id, or null for anything else. */
function nonEmptyId(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

/** Register the editor's study.* intent handlers once. */
export function registerProjectEditorIntents(): void {
  if (registered) return;
  registered = true;

  registerIntent('study.proposeLesson', (args) => {
    const content = args.content;
    // Fail-closed: non-empty content and a channel id, or we ignore it.
    if (typeof content !== 'string' || !content) return;
    const channelId = nonEmptyId(args.channel_id);
    if (!channelId) return;
    useLessonCaptureStore.getState().propose(channelId, content);
  });

  registerIntent('study.revisionNotesChanged', args => {
    const documentId = args.document_id;
    // Fail-closed: without a document id there is nothing to refetch.
    if (typeof documentId !== 'string' || !documentId) return;
    useEditorNotesSignal.getState().bump(documentId);
  });
}

/** Test-only: allow re-registration after a dispatcher reset. */
export function __resetProjectEditorIntentRegistrationForTests(): void {
  registered = false;
}
